"use client";

import React from "react";
import "./index.css";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Data imports
const annualEPS = [
  { year: "2017", eps: 0.35 },
  { year: "2018", eps: 0.08 },
  { year: "2019", eps: 0.11 },
  { year: "2020", eps: 0.24 },
  { year: "2021", eps: 0.87 },
  { year: "2022", eps: 0.5 },
  { year: "2023", eps: 0.27 },
  { year: "2024", eps: 1.28 },
];

const quarterlyEPS = [
  { quarter: "Q1 21", eps: -0.19 },
  { quarter: "Q2 21", eps: 0.32 },
  { quarter: "Q3 21", eps: -0.19 },
  { quarter: "Q4 21", eps: -0.19 },
  { quarter: "Q1 22", eps: 0.19 },
  { quarter: "Q2 22", eps: 0.13 },
  { quarter: "Q3 22", eps: 0.04 },
  { quarter: "Q4 22", eps: 0.04 },
  { quarter: "Q1 23", eps: 0.18 },
  { quarter: "Q2 23", eps: 0.34 },
  { quarter: "Q3 23", eps: 0.31 },
  { quarter: "Q4 23", eps: 0.45 },
  { quarter: "Q1 25", eps: 0.61 },
];

const bookValueData = [
  { year: "2021", q1: 12.14, q2: 1.5, q3: 1.7, q4: 2.5 },
  { year: "2022", q1: 12.73, q2: 1.1, q3: 1.4, q4: 2.1 },
  { year: "2023", q1: 12.97, q2: 0.9, q3: 1.9, q4: 2.9 },
  { year: "2024", q1: 14.27, q2: 1.2, q3: 0.6, q4: 2.2 },
  { year: "2025", q1: 14.16, q2: 1.8, q3: 1.6, q4: 2.8 },
];

const returnRatios = [
  { year: "2020", ROE: 2.14, ROA: 0.84, ROCE: -2.05 },
  { year: "2021", ROE: 7.16, ROA: 0.25, ROCE: 0.94 },
  { year: "2022", ROE: 3.91, ROA: 0.84, ROCE: 2.14 },
  { year: "2023", ROE: 2.11, ROA: -1, ROCE: -1 },
  { year: "2024", ROE: 8.98, ROA: 7.24, ROCE: 0.94 },
];

const psxStats = {
  price: 25.51,
  change: 2.12,
  changePercent: 9.06,
  volume: 5783008,
  marketCap: 20.45,
  avgVolume: 2786376,
  performance: {
    oneMonth: 1.31,
    threeMonth: -9.57,
    sixMonth: 74.97,
    oneYear: 178.19,
  },
  capital: {
    paidUp: 8014.77,
    authorized: 10000,
    totalShares: 801.48,
    freeFloat: 480.89,
    freeFloatPercent: 60.0,
  },
  ratios: {
    beta: 1.41,
    pe: 19.93,
    expectedPE: 13.86,
    expEarningsGrowth: 43.75,
    peg: 0.32,
    evps: 25.9,
    bookValuePerShare: 14.16,
    debtEquity: 1.39,
  },
  yearEnd: "June",
  faceValue: 10,
  dividendDate: "18-Oct-2024",
};

const earningsData = [
  { label: "Latest EPS Upto 2025 2Q", value: "Rs. 0.92" },
  { label: "EPS Last Quarter 2025 2Q", value: "Rs. 0.61" },
  { label: "Last Annual EPS Upto 2024 4Q", value: "Rs. 1.28" },
  { label: "Price To Earning P/E Upto 2024 4Q", value: "19.93 x" },
  { label: "Exp Price To Earning P/E Upto 2025 2Q", value: "13.86 x" },
  { label: "Exp Earning Growth Upto 2025 2Q", value: "43.75 %" },
  { label: "Price Earning Growth Upto 2025 2Q", value: "0.32 x" },
];

const ratiosData = [
  { label: "Gross Margin Upto 0", value: "N/A %" },
  { label: "Sales Growth (YoY)", value: "N/A" },
  { label: "Sales Growth (QoQ)", value: "N/A" },
  { label: "Enterprise Value Per Share Upto 2025 2Q", value: "Rs. 25.90" },
  { label: "Book Value Per Share Upto 2025 2Q", value: "Rs. 14.16" },
  { label: "Debt to Equity Ratio Upto 2024 4Q", value: "1.39 %" },
  { label: "Price Date Dividend", value: "18-Oct-2024" },
];

const summaryData = [
  {
    label: "Paid Up Capital",
    value: `Rs. ${psxStats.capital.paidUp.toFixed(2)}`,
  },
  {
    label: "Authorized Capital",
    value: `Rs. ${psxStats.capital.authorized.toLocaleString()}`,
  },
  {
    label: "Total No. Shares",
    value: `${psxStats.capital.totalShares.toLocaleString()} mn`,
  },
  {
    label: "Free Float",
    value: `${psxStats.capital.freeFloat.toLocaleString()} mn`,
  },
  { label: "Free Float %", value: `${psxStats.capital.freeFloatPercent}%` },
  { label: "Beta", value: psxStats.ratios.beta },
  { label: "Facevalue", value: `Rs. ${psxStats.faceValue}` },
  { label: "Year End", value: psxStats.yearEnd },
  { label: "Market Cap", value: `Rs. ${psxStats.marketCap} bn` },
  {
    label: "Price / Change",
    value: `Rs. ${psxStats.price.toFixed(2)} (${
      psxStats.change > 0 ? "+" : ""
    }${psxStats.change} / ${psxStats.changePercent > 0 ? "+" : ""}${
      psxStats.changePercent
    }%)`,
  },
  { label: "Volume", value: psxStats.volume.toLocaleString() },
  { label: "Avg Volume", value: psxStats.avgVolume.toLocaleString() },
  { label: "1 Year Performance", value: `${psxStats.performance.oneYear}%` },
  { label: "Dividend Date", value: psxStats.dividendDate },
];

// Custom components
const StockSummaryItem = ({ label, value }) => (
  <div className="summary-item">
    <span className="summary-label">{label}</span>
    <span className="summary-value">{value}</span>
  </div>
);

const DataRow = ({ label, value }) => (
  <div className="data-row">
    <span className="data-label">{label}</span>
    <span className="data-value">{value}</span>
  </div>
);

const PSXDashboard = () => {
  const [activeTab, setActiveTab] = React.useState("eps");

  return (
    <div className="psx1-dashboard">
      {/* Header with key metrics */}
      <div className="psx1-dashboard-header">
        <div className="psx1-container">
          <div className="psx1-header-content">
            <div className="header-title">
              <h1>Pakistan Stock Exchange</h1>
              <p>Real-time market data and financial analysis</p>
            </div>
            <div className="price-badge">
              <div className="price-value">Rs. {psxStats.price.toFixed(2)}</div>
              <div className="price-change">
                {psxStats.change > 0 ? (
                  <span className="badge positive">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="7" y1="17" x2="17" y2="7"></line>
                      <polyline points="7 7 17 7 17 17"></polyline>
                    </svg>
                    +{psxStats.change} (+{psxStats.changePercent}%)
                  </span>
                ) : (
                  <span className="badge negative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="7" y1="7" x2="17" y2="17"></line>
                      <polyline points="17 7 17 17 7 17"></polyline>
                    </svg>
                    {psxStats.change} ({psxStats.changePercent}%)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="psx1-container">
        <div className="metrics-cards">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Market Cap</h3>
            </div>
            <div className="card-content">
              <div className="metric-value">Rs. {psxStats.marketCap} bn</div>
              <p className="metric-subtitle">
                Total shares: {psxStats.capital.totalShares.toLocaleString()} mn
              </p>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Volume</h3>
            </div>
            <div className="card-content">
              <div className="metric-value">
                {psxStats.volume.toLocaleString()}
              </div>
              <p className="metric-subtitle">
                Avg: {psxStats.avgVolume.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">1 Year Performance</h3>
            </div>
            <div className="card-content">
              <div className="metric-value">
                {psxStats.performance.oneYear}%
                {psxStats.performance.oneYear > 0 ? (
                  <svg
                    className="trend-icon positive"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                    <polyline points="17 6 23 6 23 12"></polyline>
                  </svg>
                ) : (
                  <svg
                    className="trend-icon negative"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="7" y1="7" x2="17" y2="17"></line>
                    <polyline points="17 7 17 17 7 17"></polyline>
                  </svg>
                )}
              </div>
              <p className="metric-subtitle">
                6M: {psxStats.performance.sixMonth}% | 3M:{" "}
                {psxStats.performance.threeMonth}%
              </p>
            </div>
          </div>
        </div>

        {/* Stock Summary */}
        <div className="card summary-card">
          <div className="card-header">
            <h3 className="card-title">
              <svg
                className="card-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                <line x1="9" y1="9" x2="9.01" y2="9"></line>
                <line x1="15" y1="9" x2="15.01" y2="9"></line>
              </svg>
              Stock Summary
            </h3>
          </div>
          <div className="card-content">
            <div className="summary-grid">
              {summaryData.map((item, index) => (
                <StockSummaryItem
                  key={index}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div className="financial-info">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <svg
                  className="card-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                  <line x1="8" y1="16" x2="16" y2="16"></line>
                  <line x1="8" y1="8" x2="16" y2="8"></line>
                </svg>
                Earnings
              </h3>
            </div>
            <div className="card-content">
              <div className="data-rows">
                {earningsData.map((item, index) => (
                  <DataRow key={index} label={item.label} value={item.value} />
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <svg
                  className="card-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                </svg>
                Important Ratios
              </h3>
            </div>
            <div className="card-content">
              <div className="data-rows">
                {ratiosData.map((item, index) => (
                  <DataRow key={index} label={item.label} value={item.value} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="charts-section">
          <div className="tabs">
            <button
              className={`tab-button ${activeTab === "eps" ? "active" : ""}`}
              onClick={() => setActiveTab("eps")}
            >
              EPS Analysis
            </button>
            <button
              className={`tab-button ${activeTab === "book" ? "active" : ""}`}
              onClick={() => setActiveTab("book")}
            >
              Book Value
            </button>
            <button
              className={`tab-button ${
                activeTab === "returns" ? "active" : ""
              }`}
              onClick={() => setActiveTab("returns")}
            >
              Return Ratios
            </button>
          </div>

          <div className="tab-content">
            {activeTab === "eps" && (
              <div className="charts-grid">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Annual EPS</h3>
                  </div>
                  <div className="card-content">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={annualEPS}
                        margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="year" />
                        <YAxis />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                            border: "none",
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="eps"
                          fill="#f97316"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Quarterly EPS</h3>
                  </div>
                  <div className="card-content">
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart
                        data={quarterlyEPS}
                        margin={{ top: 5, right: 20, bottom: 25, left: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="quarter"
                          angle={-45}
                          textAnchor="end"
                          height={60}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                            border: "none",
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="eps"
                          stroke="#0ea5e9"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{
                            r: 6,
                            stroke: "#0ea5e9",
                            strokeWidth: 2,
                            fill: "white",
                          }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "book" && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Book Value vs Price to Book</h3>
                </div>
                <div className="card-content">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={bookValueData}
                      margin={{ top: 20, right: 30, bottom: 5, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="year" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                          border: "none",
                        }}
                      />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="q1"
                        fill="#f97316"
                        name="Book Value"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="q2"
                        fill="#0ea5e9"
                        name="Price to Book Q1"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="q3"
                        fill="#10b981"
                        name="Price to Book Q2"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="q4"
                        fill="#6366f1"
                        name="Price to Book Q3"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {activeTab === "returns" && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Return Ratios (ROE, ROA, ROCE)</h3>
                </div>
                <div className="card-content">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                      data={returnRatios}
                      margin={{ top: 20, right: 30, bottom: 5, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="year" />
                      <YAxis />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                          border: "none",
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="ROE"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{
                          r: 6,
                          stroke: "#10b981",
                          strokeWidth: 2,
                          fill: "white",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="ROA"
                        stroke="#f97316"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{
                          r: 6,
                          stroke: "#f97316",
                          strokeWidth: 2,
                          fill: "white",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="ROCE"
                        stroke="#ef4444"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{
                          r: 6,
                          stroke: "#ef4444",
                          strokeWidth: 2,
                          fill: "white",
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PSXDashboard;
